import { Microfrontend } from '@cian/mf-react';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';

import { FC, useMemo } from 'react';

import { TAdBannerMicrofrontendParameters, TAdBannerPlaceParameters } from 'shared/types/adBannerMicrofrontend';

import { AdBannerPlaceholder } from './components/AdBannerPlaceholder';
import { useAdBannerCommonParameters, useAdBannerMicrofrontend, useAdBannerMicrofrontendEvents } from './hooks';

export interface IAdBannerMicrofrontendContainerProps {
  manifest: IMicrofrontendManifest;
  parameters: TAdBannerPlaceParameters;
  onStub?(): void;
  onError?(): void;
}

/**
 * Generic-контейнер микрофронтенда рекламных баннеров: не знает ни про одно конкретное место.
 * Резолвит общие параметры (puids, регион, placeId), резервирует место под баннер и
 * монтирует микрофронтенд только на клиенте.
 */
export const AdBannerMicrofrontendContainer: FC<IAdBannerMicrofrontendContainerProps> = ({
  manifest,
  parameters,
  onStub,
  onError,
}) => {
  const { microfrontendRef, isReady } = useAdBannerMicrofrontend();
  const commonParameters = useAdBannerCommonParameters();

  useAdBannerMicrofrontendEvents({ isReady, onError, onStub, placeId: commonParameters.placeId });

  const { microfrontendName, imageVersion, runtimeName } = manifest;

  // Общие параметры — база, параметры места перекрывают её.
  // Мемоизация держит ссылку стабильной, иначе микрофронтенд ре-рендерится на каждый рендер хоста.
  const mergedParameters = useMemo<TAdBannerMicrofrontendParameters>(
    () => ({ ...commonParameters, ...parameters }),
    [parameters, commonParameters],
  );

  return (
    <AdBannerPlaceholder sizes={parameters.sizes} testId={`AdBannerMicrofrontend-${parameters.place}`}>
      {isReady && (
        <Microfrontend
          ref={microfrontendRef}
          name={microfrontendName}
          image={imageVersion}
          runtime={runtimeName}
          parameters={mergedParameters}
        />
      )}
    </AdBannerPlaceholder>
  );
};
